import { useEffect, useState } from "react";
import { Loader2, Trash2, Clock, UserPlus, Crosshair, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SPOT_STATUS, NIVEAU_PARTENARIAT, frenchPhone, statusSinceLabel } from "@/lib/shariti";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// Statuts pour lesquels une raison explicite est demandée (ex: pourquoi blacklisté ?)
const REASON_STATUSES = ["blackliste", "ferme", "refus_definitif"];

const emptyContact = () => ({
  civilite: "", prenom: "", nom: "", fonction: "",
  telephone: "", telephone_2: "", telephone_3: "", email: "", email_2: "",
});

// Normalise un contact reçu du backend (0 initial sur les tél) pour l'édition.
const loadContact = (c) => ({
  ...emptyContact(),
  civilite: c?.civilite || "", prenom: c?.prenom || "", nom: c?.nom || "", fonction: c?.fonction || "",
  telephone: frenchPhone(c?.telephone) || "", telephone_2: frenchPhone(c?.telephone_2) || "",
  telephone_3: frenchPhone(c?.telephone_3) || "", email: c?.email || "", email_2: c?.email_2 || "",
});

// Convertit un contact d'édition -> payload (chaînes vides => null).
const dumpContact = (c) => {
  const n = (v) => (v && String(v).trim() ? String(v).trim() : null);
  return {
    civilite: n(c.civilite), prenom: n(c.prenom), nom: n(c.nom), fonction: n(c.fonction),
    telephone: n(c.telephone), telephone_2: n(c.telephone_2), telephone_3: n(c.telephone_3),
    email: n(c.email), email_2: n(c.email_2),
  };
};

const isEmptyContact = (c) => Object.values(dumpContact(c)).every((v) => v === null);

/** Éditeur compact d'un contact (civilité/prénom/nom/fonction + 3 tél + 2 emails). */
function ContactEditor({ value, onChange, prefix }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e?.target ? e.target.value : e });
  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-[110px_1fr_1fr]">
        <div>
          <Label className="text-[11px]">Civilité</Label>
          <Select value={value.civilite || "none"} onValueChange={(v) => onChange({ ...value, civilite: v === "none" ? "" : v })}>
            <SelectTrigger className="mt-1 h-9" data-testid={`${prefix}-civilite`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              <SelectItem value="M.">Monsieur (M.)</SelectItem>
              <SelectItem value="Mme">Madame (Mme)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-[11px]">Prénom</Label><Input className="mt-1 h-9" value={value.prenom} onChange={set("prenom")} data-testid={`${prefix}-prenom`} /></div>
        <div><Label className="text-[11px]">Nom</Label><Input className="mt-1 h-9" value={value.nom} onChange={set("nom")} data-testid={`${prefix}-nom`} /></div>
      </div>
      <div>
        <Label className="text-[11px]">Fonction</Label>
        <Input className="mt-1 h-9" value={value.fonction} onChange={set("fonction")} placeholder="Directeur, Adjoint, Standard…" data-testid={`${prefix}-fonction`} />
      </div>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
        <div><Label className="text-[11px]">Téléphone 1</Label><Input className="mt-1 h-9" value={value.telephone} onChange={set("telephone")} placeholder="0612345678" data-testid={`${prefix}-tel`} /></div>
        <div><Label className="text-[11px]">Téléphone 2</Label><Input className="mt-1 h-9" value={value.telephone_2} onChange={set("telephone_2")} placeholder="Mobile directeur…" data-testid={`${prefix}-tel2`} /></div>
        <div><Label className="text-[11px]">Téléphone 3</Label><Input className="mt-1 h-9" value={value.telephone_3} onChange={set("telephone_3")} placeholder="Standard…" data-testid={`${prefix}-tel3`} /></div>
      </div>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        <div><Label className="text-[11px]">Email 1</Label><Input className="mt-1 h-9" value={value.email} onChange={set("email")} placeholder="contact@enseigne.fr" data-testid={`${prefix}-email`} /></div>
        <div><Label className="text-[11px]">Email 2</Label><Input className="mt-1 h-9" value={value.email_2} onChange={set("email_2")} data-testid={`${prefix}-email2`} /></div>
      </div>
    </div>
  );
}

export const EditSpotDialog = ({ open, onClose, spot, onSaved }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [f, setF] = useState({});
  const [cp, setCp] = useState(emptyContact());
  const [cs, setCs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (spot) {
      setF({
        status: spot.status, niveau_partenariat: spot.niveau_partenariat,
        consignes: spot.consignes || "", consignes_source: spot.consignes_source || null,
        statut_raison: spot.statut_raison || "",
        is_payant: spot.is_payant, montant_payant: spot.montant_payant || "",
        jour_off_responsable: spot.jour_off_responsable || "",
        flux: spot.flux?.global_value || "", notes: spot.notes || "",
        enseigne: spot.enseigne || "", ville: spot.ville || "",
        arrondissement: spot.arrondissement || "", rue: spot.rue || "",
        nom_donne: spot.nom_donne || "", address_full: spot.address_full || "",
        lat: spot.gps?.lat != null ? String(spot.gps.lat) : "",
        lng: spot.gps?.lng != null ? String(spot.gps.lng) : "",
        rayon: spot.pointage_rayon_m != null ? String(spot.pointage_rayon_m) : "",
        _rayon_defaut: spot.pointage_rayon_defaut ?? 300,
        _consignes_initial: spot.consignes || "",
      });
      setCp(loadContact(spot.contact_principal));
      setCs((spot.contacts_secondaires || []).map(loadContact));
    }
  }, [spot, open]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const addContact = () => setCs((p) => [...p, emptyContact()]);
  const removeContact = (i) => setCs((p) => p.filter((_, idx) => idx !== i));
  const patchContact = (i) => (v) => setCs((p) => p.map((c, idx) => (idx === i ? v : c)));

  const save = async () => {
    // --- Géolocalisation & rayon (ADMIN uniquement) : validation AVANT envoi ---
    const geoPayload = {};
    if (isAdmin) {
      const latStr = String(f.lat ?? "").trim();
      const lngStr = String(f.lng ?? "").trim();
      if (latStr === "" && lngStr === "") {
        geoPayload.gps = null;
      } else if (latStr === "" || lngStr === "") {
        toast.error("Coordonnées incomplètes : renseignez la latitude ET la longitude.");
        return;
      } else {
        const lat = Number(latStr), lng = Number(lngStr);
        if (Number.isNaN(lat) || Number.isNaN(lng)) { toast.error("Coordonnées invalides : nombres attendus."); return; }
        if (lat < -90 || lat > 90) { toast.error("Latitude invalide : doit être comprise entre -90 et 90."); return; }
        if (lng < -180 || lng > 180) { toast.error("Longitude invalide : doit être comprise entre -180 et 180."); return; }
        geoPayload.gps = { lat, lng };
      }
      const rStr = String(f.rayon ?? "").trim();
      if (rStr === "") {
        geoPayload.pointage_rayon_m = null;
      } else {
        const r = Number(rStr);
        if (!Number.isInteger(r) || r <= 0) { toast.error("Rayon invalide : entier en mètres supérieur à 0."); return; }
        geoPayload.pointage_rayon_m = r;
      }
    }
    setSaving(true);
    try {
      const consignesEdited = (f.consignes || "") !== (f._consignes_initial || "");
      const payload = {
        status: f.status, niveau_partenariat: f.niveau_partenariat,
        consignes: f.consignes,
        consignes_source: consignesEdited ? "equipe" : f.consignes_source,
        statut_raison: REASON_STATUSES.includes(f.status) ? (f.statut_raison || null) : null,
        is_payant: f.is_payant, montant_payant: f.montant_payant ? Number(f.montant_payant) : null,
        jour_off_responsable: f.jour_off_responsable, notes: f.notes,
        enseigne: f.enseigne, ville: f.ville, arrondissement: f.arrondissement,
        rue: f.rue, nom_donne: f.nom_donne, address_full: f.address_full,
        flux: { ...(spot.flux || {}), mode: "global", global_value: f.flux ? Number(f.flux) : null },
        contact_principal: dumpContact(cp),
        contacts_secondaires: cs.filter((c) => !isEmptyContact(c)).map(dumpContact),
        ...geoPayload,
      };
      await api.put(`/spots/${spot.id}`, payload);
      toast.success("Spot mis à jour");
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  if (!spot) return null;
  const needsReason = REASON_STATUSES.includes(f.status);
  const statusChanged = f.status !== spot.status;
  const consigneImported = f.consignes_source === "import" && (f.consignes || "") === (f._consignes_initial || "");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="pr-6 text-base">Modifier le spot — {spot.normalized_name}</DialogTitle></DialogHeader>

        <div className="space-y-5">
          {/* Nomenclature interne : ENSEIGNE VILLE(+ARR) RUE NOM DONNÉ */}
          <section className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Nom du spot (nomenclature interne)</div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div><Label className="text-[11px]">Enseigne</Label><Input className="mt-1 h-9" value={f.enseigne} onChange={set("enseigne")} placeholder="Monoprix" data-testid="edit-enseigne" /></div>
              <div><Label className="text-[11px]">Ville</Label><Input className="mt-1 h-9" value={f.ville} onChange={set("ville")} placeholder="Paris" data-testid="edit-ville" /></div>
              <div><Label className="text-[11px]">Arrond.</Label><Input className="mt-1 h-9" value={f.arrondissement} onChange={set("arrondissement")} placeholder="14" data-testid="edit-arr" /></div>
              <div><Label className="text-[11px]">Rue</Label><Input className="mt-1 h-9" value={f.rue} onChange={set("rue")} placeholder="Général Leclerc" data-testid="edit-rue" /></div>
            </div>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div><Label className="text-[11px]">Nom donné</Label><Input className="mt-1 h-9" value={f.nom_donne} onChange={set("nom_donne")} placeholder="Daguerre" data-testid="edit-nom-donne" /></div>
              <div><Label className="text-[11px]">Adresse complète</Label><Input className="mt-1 h-9" value={f.address_full} onChange={set("address_full")} placeholder="12 Rue…, 75014 Paris" data-testid="edit-address" /></div>
            </div>
            <div className="mt-2 rounded-lg bg-white px-2.5 py-1.5 text-[11px]" data-testid="edit-name-preview">
              <span className="text-muted-ink">Aperçu du nom : </span>
              <b className="text-ink">{[f.enseigne, `${f.ville || ""}${f.arrondissement ? " " + f.arrondissement : ""}`.trim(), f.rue, f.nom_donne].filter(Boolean).join(" ").toUpperCase() || "—"}</b>
            </div>
          </section>

          {/* Géolocalisation & rayon de pointage — ADMIN uniquement */}
          {isAdmin && (
            <section className="rounded-xl border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.05)] p-3" data-testid="edit-geo-section">
              <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]">
                <Crosshair className="h-3.5 w-3.5" /> Géolocalisation &amp; pointage (admin)
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div><Label className="text-[11px]">Latitude</Label><Input className="mt-1 h-9" value={f.lat} onChange={set("lat")} inputMode="decimal" placeholder="48.852094" data-testid="edit-lat" /></div>
                <div><Label className="text-[11px]">Longitude</Label><Input className="mt-1 h-9" value={f.lng} onChange={set("lng")} inputMode="decimal" placeholder="2.415901" data-testid="edit-lng" /></div>
                <div><Label className="text-[11px]">Rayon (m)</Label><Input className="mt-1 h-9" value={f.rayon} onChange={set("rayon")} inputMode="numeric" placeholder={`défaut ${f._rayon_defaut}`} data-testid="edit-rayon" /></div>
              </div>
              <p className="mt-2 flex items-start gap-1 text-[11px] leading-snug text-muted-ink" data-testid="edit-geo-hint">
                <ShieldAlert className="mt-0.5 h-3 w-3 shrink-0 text-[var(--shariti-orange)]" />
                Latitude −90 à 90 · longitude −180 à 180 · rayon entier positif. Rayon vide = défaut global ({f._rayon_defaut} m). Coordonnées vides = GPS effacé (le pointage géolocalisé sera alors bloqué).
              </p>
            </section>
          )}

          {/* Statut & partenariat */}
          <section className="space-y-2.5">
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <div><Label className="text-[11px]">Statut</Label>
                <Select value={f.status} onValueChange={set("status")}><SelectTrigger className="mt-1 h-9" data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(SPOT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-[11px]">Niveau partenariat</Label>
                <Select value={f.niveau_partenariat} onValueChange={set("niveau_partenariat")}><SelectTrigger className="mt-1 h-9" data-testid="edit-niveau"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(NIVEAU_PARTENARIAT).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="flex items-center gap-1.5 rounded-lg bg-[var(--surface-2)] px-2.5 py-1.5 text-[11px] text-muted-ink" data-testid="edit-status-since">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              {statusChanged
                ? <span>Nouveau statut — <b className="text-ink">sera horodaté automatiquement</b> à l'enregistrement.</span>
                : <span>Statut actuel : <b className="text-ink">{statusSinceLabel(spot)}</b></span>}
            </div>
            {needsReason && (
              <div className="rounded-lg border border-[rgba(214,69,69,0.28)] bg-[rgba(214,69,69,0.05)] p-2.5">
                <Label className="text-[11px] font-semibold text-[#9B2226]">Raison du statut « {SPOT_STATUS[f.status]?.label} »</Label>
                <Input value={f.statut_raison} onChange={set("statut_raison")} data-testid="edit-statut-raison"
                  placeholder="Ex : magasin définitivement fermé / directeur refuse / travaux…" className="mt-1 h-9" />
              </div>
            )}
          </section>

          {/* Contact principal */}
          <section>
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Contact principal</div>
            <ContactEditor value={cp} onChange={setCp} prefix="edit" />
          </section>

          {/* Contacts secondaires */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Autres contacts</div>
              <Button type="button" variant="ghost" size="sm" onClick={addContact} data-testid="add-contact"
                className="h-7 text-[11px] font-semibold text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.1)]">
                <UserPlus className="mr-1 h-3.5 w-3.5" /> Ajouter un contact
              </Button>
            </div>
            {cs.length === 0 ? (
              <p className="text-[11px] text-muted-ink">Aucun contact secondaire. Ajoutez le directeur, l'adjoint, le standard…</p>
            ) : (
              <div className="space-y-3">
                {cs.map((c, i) => (
                  <div key={i} className="rounded-xl border border-[var(--border-soft)] p-3" data-testid={`contact-card-${i}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-[11px] font-bold text-muted-ink">Contact {i + 2}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeContact(i)} data-testid={`remove-contact-${i}`}
                        className="h-7 w-7 p-0 text-muted-ink hover:bg-[rgba(214,69,69,0.1)] hover:text-[#9B2226]" title="Supprimer ce contact">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <ContactEditor value={c} onChange={patchContact(i)} prefix={`edit-c${i}`} />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Flux / jour off */}
          <section className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            <div><Label className="text-[11px]">Flux clients/jour</Label><Input type="number" className="mt-1 h-9" value={f.flux} onChange={set("flux")} data-testid="edit-flux" /></div>
            <div><Label className="text-[11px]">Jour off responsable</Label><Input className="mt-1 h-9" value={f.jour_off_responsable} onChange={set("jour_off_responsable")} placeholder="lundi" data-testid="edit-jouroff" /></div>
          </section>

          {/* Consignes + notes */}
          <section className="space-y-2.5">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[11px]">Consignes</Label>
                {consigneImported && <span className="rounded-full bg-[rgba(252,219,83,0.4)] px-2 py-0.5 text-[10px] font-bold uppercase text-[#5A4200]" data-testid="consigne-import-badge">Importé — à revalider</span>}
              </div>
              <Textarea className="mt-1" value={f.consignes} onChange={set("consignes")} data-testid="edit-consignes" />
            </div>
            <div><Label className="text-[11px]">Notes</Label><Textarea className="mt-1" value={f.notes} onChange={set("notes")} data-testid="edit-notes" /></div>
          </section>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="edit-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
